import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Image as ImageIcon, Star } from "lucide-react";
import {
  getFavoriteChipStyle,
  getSourceDisplay,
  isFavoriteTag,
  FAVORITE_LABEL,
  type ReadwiseLibraryMatch,
  type ReadwiseHighlightRow,
} from "@/lib/readwiseSource";
import { safeHref } from "@/lib/safeHref";
import { deriveLibraryTitle } from "@/lib/library";
import { getSourceRowToneVars, readwiseTokenToLibraryType } from "@/lib/sourceTypeRowTone";

export const HighlightCard = ({ r, libraryMatch = null, returnTo = "readwise-highlights" }: { r: ReadwiseHighlightRow; libraryMatch?: ReadwiseLibraryMatch | null; returnTo?: string }) => {
  const src = getSourceDisplay(r.book_source);
  const SrcIcon = src.Icon;
  const tone = getSourceRowToneVars(readwiseTokenToLibraryType(src.tokenKey));
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const handleMakeImage = () => {
    sessionStorage.setItem(
      "excerptus.imageStudioPrefill",
      JSON.stringify({
        bodyHtml: DOMPurify.sanitize(r.highlight_text),
        author: r.book_author ?? "",
        publication: r.book_title ?? "",
      }),
    );
    navigate("/image-studio");
  };
  const sourceChipStyle = {
    backgroundColor: "transparent",
    color: tone.title,
    borderColor: tone.selected,
    borderWidth: 1,
  } as const;
  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? tone.hover : tone.idle,
        borderLeft: `4px solid ${tone.accent}`,
      }}
      className="shadow-soft transition-shadow"
    >
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-base leading-relaxed whitespace-pre-wrap text-muted-foreground flex-1">{r.highlight_text}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMakeImage}
            title="Send to Image Studio"
            className={`shrink-0 transition-opacity ${hovered ? "opacity-100" : "opacity-0"} hidden md:inline-flex`}
          >
            <ImageIcon className="h-4 w-4" /> Make image
          </Button>
        </div>
        {r.note && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3">
            Note: {r.note}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {r.book_title && <span className="font-medium" style={{ color: "hsl(var(--foreground))" }}>{r.book_title}</span>}
          {r.book_author && <span>· {r.book_author}</span>}
          <Badge
            variant="outline"
            className="text-xs gap-1 border"
            style={sourceChipStyle}
          >
            <SrcIcon className="h-3 w-3" />
            {src.label}
          </Badge>
          {r.readwise_tags.map((t) =>
            isFavoriteTag(t) ? (
              <Badge
                key={t}
                variant="outline"
                className="text-xs gap-1 border"
                style={getFavoriteChipStyle()}
              >
                <Star className="h-3 w-3 fill-current" />
                {FAVORITE_LABEL}
              </Badge>
            ) : (
              <Badge key={t} variant="secondary" className="text-xs">
                {t}
              </Badge>
            ),
          )}
          {r.location &&
            r.location_type?.toLowerCase() !== "order" &&
            !(!r.location_type && /^\d+$/.test(r.location)) && (
              <span>
                · {r.location_type ?? "loc"} {r.location}
              </span>
            )}
          {r.highlighted_at && (
            <span>· {new Date(r.highlighted_at).toLocaleDateString()}</span>
          )}
          {(() => { const safe = safeHref(r.readwise_url); return safe ? (
            <a
              href={safe}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              View in Readwise <ExternalLink className="h-3 w-3" />
            </a>
          ) : null; })()}
        </div>
        {libraryMatch ? (
          <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
            <p className="font-medium text-foreground">Matching source record found</p>
            <p className="text-muted-foreground">{deriveLibraryTitle(libraryMatch)}{libraryMatch.author_text ? ` — ${libraryMatch.author_text}` : ""}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <a href={`/my-library?item=${libraryMatch.id}&returnTo=${encodeURIComponent(returnTo)}`} className="text-primary hover:underline">Open source</a>
              <a href={`/new-excerpt?libraryItem=${libraryMatch.id}&returnTo=${encodeURIComponent(returnTo)}`} className="text-primary hover:underline">Create excerpt from this source</a>
              <a href={`/my-library?returnTo=${encodeURIComponent(returnTo)}`} className="text-primary hover:underline">Create new source</a>
            </div>
          </div>
        ) : null}
        <div className="flex md:hidden pt-1 -mb-1 border-t border-border/50 -mx-1 px-1 mt-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-9 flex-1 justify-center text-xs"
            onClick={handleMakeImage}
          >
            <ImageIcon className="h-4 w-4 mr-1" /> Make image
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
